import type { IconDetail } from '~~/shared/types/menu'

/**
 * Allergen sub-categories grouped under a parent allergen.
 *
 * Single source of truth for the parent/sub relationship. This is hardcoded on
 * purpose: the API delivers each icon by name (no database id), and these
 * canonical ids match:
 * - normalizeIcon() output (lowercase Dutch names)
 * - i18n keys under allergy.*
 * - the parent SVG filename in /public/images/icons/ (subs reuse the parent SVG)
 *
 * If the API later provides a numeric id, only this file and the mapping in
 * normalizeIcon need to change; the rest of the app reads through the helpers.
 */
export const ALLERGEN_HIERARCHY = {
  noten: ['amandel', 'hazelnoot', 'walnoot', 'cashewnoot', 'pecannoot', 'paranoot', 'pistachenoot', 'macadamianoot'],
  gluten: ['tarwe', 'rogge', 'gerst', 'haver'],
  // Additives are grouped like a parent in the legend and filter, but — unlike
  // noten/gluten — the "additief" parent is synthetic: it never arrives as an
  // icon from the API, and on a dish each additive renders as its own chip
  // (see isAdditive + groupItemIcons) instead of collapsing under the parent.
  additief: ['kleurstof', 'conserveermiddel', 'antioxidant', 'smaakversterker', 'gezwaveld', 'gezwart', 'fosfaat', 'zoetstof', 'fenylalanine', 'cafeine', 'kinine'],
} as const satisfies Record<string, readonly string[]>

/** The synthetic parent id that groups the additive sub-categories. */
export const ADDITIVE_GROUP_ID = 'additief'

/**
 * Count-badge circle colour per group, matching each group icon's own fill.
 * Hardcoded on purpose: the badge must look identical across every theme, so it
 * bypasses the theme system. Only groups that show a badge need an entry. The
 * number itself stays white (set in the `iconTooltip.badge` theme slot).
 */
export const GROUP_BADGE_COLORS: Record<string, string> = {
  noten: '#85532F',
  gluten: '#BF9B49',
  additief: '#063073',
}

/** Reverse lookup: sub id -> parent id */
const SUB_TO_PARENT: Record<string, string> = Object.entries(ALLERGEN_HIERARCHY)
  .reduce<Record<string, string>>((acc, [parent, subs]) => {
    for (const sub of subs) acc[sub] = parent
    return acc
  }, {})

/** True when the id is a parent that can hold sub-categories (noten/gluten). */
export function isParent(id: string): boolean {
  return id in ALLERGEN_HIERARCHY
}

/** True when the id is a sub-category (e.g. hazelnoot). */
export function isSub(id: string): boolean {
  return id in SUB_TO_PARENT
}

/** Parent id for a sub-category, or undefined for a non-sub id. */
export function parentOf(id: string): string | undefined {
  return SUB_TO_PARENT[id]
}

/** True when the id is an additive (a sub of the synthetic "additief" parent). */
export function isAdditive(id: string): boolean {
  return SUB_TO_PARENT[id] === ADDITIVE_GROUP_ID
}

/** All possible sub ids for a parent id (regardless of what the menu contains). */
export function childrenOf(id: string): readonly string[] {
  return ALLERGEN_HIERARCHY[id as keyof typeof ALLERGEN_HIERARCHY] ?? []
}

// --- Grouping helpers ---------------------------------------------------------

export interface AllergenSingleEntry {
  type: 'single'
  icon: IconDetail
}

export interface AllergenGroupEntry {
  type: 'group'
  parentId: string
  /** The parent icon when present in the menu list, else null (derived). */
  parentIcon: IconDetail | null
  children: IconDetail[]
}

export type AllergenEntry = AllergenSingleEntry | AllergenGroupEntry

/**
 * Turns a flat icon list (menu-wide) into a tree: singles stay flat, subs are
 * nested under their parent at the parent's position. A parent with no subs
 * present in this menu collapses back to a single (plain, filterable) entry.
 * Used by the filter modal and the legend.
 */
export function buildAllergenTree(icons: IconDetail[]): AllergenEntry[] {
  const entries: AllergenEntry[] = []
  const groups = new Map<string, AllergenGroupEntry>()

  const ensureGroup = (parentId: string): AllergenGroupEntry => {
    let group = groups.get(parentId)
    if (!group) {
      group = { type: 'group', parentId, parentIcon: null, children: [] }
      groups.set(parentId, group)
      entries.push(group)
    }
    return group
  }

  for (const icon of icons) {
    if (isParent(icon.id)) {
      ensureGroup(icon.id).parentIcon = icon
    }
    else if (isSub(icon.id)) {
      ensureGroup(parentOf(icon.id)!).children.push(icon)
    }
    else {
      entries.push({ type: 'single', icon })
    }
  }

  // Collapse parents that have no sub-categories in this menu back to a plain
  // single entry so you can still filter on the generic allergen on its own.
  return entries.map((entry) => {
    if (entry.type === 'group' && entry.children.length === 0 && entry.parentIcon) {
      return { type: 'single', icon: entry.parentIcon } satisfies AllergenSingleEntry
    }
    return entry
  })
}

export interface DishIconChip {
  /** Icon id used for the SVG + primary label (a parent or a standalone icon). */
  iconId: string
  /** Present sub ids grouped under this parent (empty for standalone icons). */
  subIds: string[]
}

/**
 * Groups a dish's flat icon ids into display chips: subs (noten/gluten AND
 * additives) collapse under their parent — one chip with the parent icon and a
 * count badge (subIds.length) — while standalone icons pass through as their
 * own chip. The additive subs collapse under the synthetic `additief` parent
 * exactly like noten/gluten.
 */
export function groupItemIcons(iconIds: string[]): DishIconChip[] {
  const chips: DishIconChip[] = []
  const byParent = new Map<string, DishIconChip>()

  const ensureChip = (iconId: string): DishIconChip => {
    let chip = byParent.get(iconId)
    if (!chip) {
      chip = { iconId, subIds: [] }
      byParent.set(iconId, chip)
      chips.push(chip)
    }
    return chip
  }

  for (const id of iconIds) {
    if (isParent(id)) {
      ensureChip(id)
    }
    else if (isSub(id)) {
      const chip = ensureChip(parentOf(id)!)
      if (!chip.subIds.includes(id)) chip.subIds.push(id)
    }
    else {
      chips.push({ iconId: id, subIds: [] })
    }
  }

  return chips
}
